import React, { Fragment, useEffect, useContext } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import { Drawer, Paper, Typography, Hidden } from '@material-ui/core';
import BusinessIcon from '@material-ui/icons/Business';
import useRouter from 'utils/useRouter';
import { Navigation } from 'components';
import navigationConfig from './navigationConfig';
import authContext from '../../../../contexts/AuthContext';

const BRAND = '#682622';

const useStyles = makeStyles(theme => ({
  root: {
    height: '100%',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#fff',
    borderRight: '1px solid #f0f0f0',
  },
  brandStrip: {
    height: 3,
    backgroundColor: BRAND,
    flexShrink: 0,
  },
  orgStrip: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1.25, 2),
    backgroundColor: '#fdf9f9',
    borderBottom: '1px solid #f0f0f0',
    flexShrink: 0,
  },
  orgIcon: {
    width: 28,
    height: 28,
    borderRadius: 7,
    backgroundColor: `${BRAND}14`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& svg': { fontSize: '0.95rem', color: BRAND },
  },
  orgLabel: {
    fontSize: '0.62rem',
    fontWeight: 600,
    color: '#aaa',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    lineHeight: 1,
  },
  orgName: {
    fontSize: '0.8rem',
    fontWeight: 700,
    color: BRAND,
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  navContent: {
    padding: theme.spacing(1.5, 1, 2),
    flex: 1,
    overflowY: 'auto',
  },
}));

const NavBar = ({ openMobile, onMobileClose, className, ...rest }) => {
  const classes = useStyles();
  const router = useRouter();
  const [{ organization }] = useContext(authContext);

  useEffect(() => {
    if (openMobile && onMobileClose) onMobileClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.location.pathname]);

  const navbarContent = (
    <>
      <div className={classes.brandStrip} />
      <div className={classes.orgStrip}>
        <div className={classes.orgIcon}><BusinessIcon /></div>
        <div style={{ minWidth: 0 }}>
          <Typography className={classes.orgLabel}>Organisation</Typography>
          <Typography className={classes.orgName}>{organization || '—'}</Typography>
        </div>
      </div>
      <div className={classes.navContent}>
        {navigationConfig.map(list => (
          <Navigation
            component="div"
            key={list.title}
            pages={list.pages}
            title={list.title}
          />
        ))}
      </div>
    </>
  );

  return (
    <Fragment>
      <Hidden lgUp>
        <Drawer anchor="left" onClose={onMobileClose} open={openMobile} variant="temporary">
          <div {...rest} className={clsx(classes.root, className)} style={{ width: 256 }}>
            {navbarContent}
          </div>
        </Drawer>
      </Hidden>
      <Hidden mdDown>
        <Paper {...rest} className={clsx(classes.root, className)} elevation={0} square>
          {navbarContent}
        </Paper>
      </Hidden>
    </Fragment>
  );
};

NavBar.propTypes = {
  className: PropTypes.string,
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool
};

export default NavBar;
